'use client'
import { Carousel } from 'flowbite-react'
import { useCallback, useState } from 'react'
import { SmartLink } from '@/payload-types'
import Link from 'next/link'
import ImageComponent from '../media/imageComponent'
import { SnippetComponent } from '../snippets/snippet'
import PopupComponent from '../popup/popup'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react'

interface IProps {
  text?: (string | undefined)[] | undefined
  smartLink: SmartLink
  imageTrigger?: React.ReactNode
  onImageClick?: () => void
}

const PAYLOAD_URL = process.env.NEXT_PUBLIC_PAYLOAD_URL ?? ''

const customCarouselTheme = {
  root: {
    base: 'relative h-full w-full',
    leftControl:
      'font-bold absolute -left-1 top-0 flex h-full items-center justify-center focus:outline-0',
    rightControl:
      'font-bold absolute -right-1 top-0 flex h-full items-center justify-center focus:outline-0',
  },
  indicators: {
    active: {
      off: 'bg-gray-400 hover:bg-gray-700 dark:bg-gray-800/50 dark:hover:bg-gray-800 group-focus:outline-0',
      on: 'bg-gray-600 dark:bg-gray-800',
    },
    base: 'h-2 w-2 rounded-full',
    wrapper: 'absolute bottom-5 left-1/2 flex -translate-x-1/2 space-x-3',
  },
  item: {
    base: 'absolute left-1/2 top-1/2 block w-full -translate-x-1/2 -translate-y-1/2',
    wrapper: {
      off: 'w-full flex-shrink-0 transform cursor-default snap-center',
      on: 'w-full flex-shrink-0 transform cursor-grab snap-center',
    },
  },
  control: {
    base: 'inline-flex items-center justify-center rounded-full group-focus:outline-0 h-6 w-6 rounded-full bg-gray-200 opacity-60 hover:bg-gray-300',
    icon: 'text-gray-600 h-4 w-4 font-extrabold !stroke-[3px]',
  },
  scrollContainer: { base: 'flex h-full', snap: 'snap-x' },
}

export const SmartLinksComponent = ({ text, smartLink, imageTrigger }: IProps) => {
  const validImages = (smartLink.screenshot ?? []).filter(
    (item) => typeof item === 'object' && item?.sizes?.webp?.url,
  )

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [isSnippetOpen, setIsSnippetOpen] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [isTooltipHovered, setIsTooltipHovered] = useState(false)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), flip({ fallbackAxisSideDirection: 'none' }), shift()],
  })

  const hover = useHover(context, { move: false, delay: { open: 0, close: 150 } })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  const handleImageClick = useCallback(
    (src: string) => {
      setSelectedImage(src)
      setIsPopoverOpen(true)
      setIsSnippetOpen(false)
      setIsOpen(false)
      setIsTooltipHovered(false)
    },
    []
  )

  const handlePopoverClose = () => {
    setIsPopoverOpen(false)
    setIsSnippetOpen(true)
  }

  const renderImages = () => {
    if (!validImages.length) return null

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderButton = (item: any, index: number) => {
      const src = `${PAYLOAD_URL}${item.sizes?.webp?.url || ''}`

      return (
        <button
          className="block pt-1 pb-2 h-[230px]"
          key={index}
          onClick={() => handleImageClick(src)}
        >
          <ImageComponent src={src} alt="image" />
        </button>
      )
    }

    return validImages.length > 1 ? (
      <Carousel theme={customCarouselTheme} slide={false} indicators className="!border-0">
        {validImages.map(renderButton)}
      </Carousel>
    ) : (
      validImages.map((item, index) => (
        <span key={index} className="max-w-full flex pt-1 pb-2 h-[230px] justify-center">
          {renderButton(item, index)}
        </span>
      ))
    )
  }

  const renderLinks = () => (
    <>
      {smartLink.externalLink && <LinkRow label="original link:" href={smartLink.externalLink} />}
      {smartLink.archiveLink && <LinkRow label="archive link:" href={smartLink.archiveLink} />}
    </>
  )

  const LinkRow = ({ label, href }: { label: string; href: string }) => (
    <span className="break-all line-clamp-1">
      <span className="font-medium text-gray-600">{label}</span>{' '}
      <Link className="text-blue-700 hover:underline" href={href} target="_blank">
        {href}
      </Link>
    </span>
  )

  const ContentBlock = () => (
    <span className="block smartLinks px-3 py-2 text-sm">
      <span className="block h-[230px]">{renderImages()}</span>
      {renderLinks()}
    </span>
  )

  return (
    <>
      {imageTrigger ? (
        <>
          <div ref={refs.setReference} {...getReferenceProps()}>
            {imageTrigger}
          </div>
          <FloatingPortal>
            {(isOpen || isTooltipHovered) && (
              <div
                className="border border-black rounded-lg w-[316px] z-50 bg-white"
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
                onMouseEnter={() => setIsTooltipHovered(true)}
                onMouseLeave={() => setIsTooltipHovered(false)}
              >
                <ContentBlock />
              </div>
            )}
          </FloatingPortal>
        </>
      ) : (
        <>
          {!isSnippetOpen && <span className="font-bold">{smartLink.textToDisplay}</span>}
          {smartLink.textToDisplay && isSnippetOpen && (
            <SnippetComponent
              text={text}
              css="border border-black rounded-lg w-[316px] z-50 bg-white"
            >
              <ContentBlock />
            </SnippetComponent>
          )}
        </>
      )}

      {isPopoverOpen && selectedImage && (
        <PopupComponent onClose={handlePopoverClose}>
          <ImageComponent src={selectedImage} alt="Selected image" />
        </PopupComponent>
      )}
    </>
  )
}

export default SmartLinksComponent
